






























































































































import { Component, Inject, Mixins } from "vue-property-decorator";

// Mixins
import HasValidation from "@/mixins/HasValidation";

// Components
import CheckoutProvider from "@/components/app/global/baskets/basket/checkoutProvider.vue";
import SelectPaymentMethod from "@/components/app/global/payments/selectPaymentMethod/index.vue";

// Enums/Data
import { BasketTypes } from "@/data/constants";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import { IShopProviderMethods } from "@/models/providers/basket/shop";
import Context from "@/mixins/context";
import { ValidationObserver } from "vee-validate";
import { ICheckoutProviderData } from "@/models/providers/checkout";
import { GatewayContext } from "@/data/enums/gateway";

@Component({
  components: {
    "checkout-provider": CheckoutProvider,
    "select-payment-method": SelectPaymentMethod,
    "checkout-control": () =>
      import(
        "@/components/app/global/baskets/basket/checkout/checkoutControl.vue"
      ),
    "guest-email": () =>
      import("@/components/app/global/baskets/basket/checkout/guestEmail.vue"),
    "billable-entities-provider": () =>
      import("@/components/app/global/client/billableEntitiesProvider.vue"),
    "billable-entities": () =>
      import("@/components/app/global/client/billableEntities.vue"),
    "cprod-billable-entity-address-search": () =>
      import("@/components/app/global/client/addressSearch.vue"),
    "billable-entities-control": () =>
      import("@/components/app/global/client/billableEntitiesControl.vue")
  }
})
export default class BasketCheckout extends Mixins(Context, HasValidation) {
  // Refs

  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    checkoutProvider: CheckoutProvider;
  };

  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly basketProviderData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  @Inject({ from: "shopProviderMethods" })
  public readonly $shopMethods!: IShopProviderMethods;

  // Data

  public isLoading = true;

  // Computed

  public get basketData() {
    return this.basketProviderData();
  }

  public get basket() {
    return this.basketData.basket;
  }

  public get basketAddressId() {
    return this.basket.address_id;
  }

  public get basketCompanyId() {
    return this.basket.company_id;
  }

  public get basketClientId() {
    return this.basketData.client?.id || this.basketData.clientId;
  }

  // Methods

  public getSelectPaymentMethodBindings($checkoutData: ICheckoutProviderData) {
    return {
      accountId: this.basketData.accountId,
      autoSelectFirstStoredPaymentMethod: !$checkoutData.paymentDetailIdFromExternalAuth,
      brandId: this.basketData.basketBrandId,
      canChangeManualPaymentamount: this.isAdmin,
      cardHolderName: this.basketData.client?.fullname,
      clientId: this.basketData.clientId,
      currencyId: this.basketData.currencyId,
      excludedGatewayStoreTypes: $checkoutData.excludedGatewayStoreTypes,
      externalAuthReturnLocation: $checkoutData.externalAuthReturnLocation,
      gatewayContext: GatewayContext.PAY,
      hasManualpayment: this.isAdmin,
      hasPartialPayment: $checkoutData.hasPartialPayment,
      hasPayLater: $checkoutData.hasPayLater,
      isDisabled: this.basketData.isProcessing,
      preselectedStoredPaymentMethodId:
        $checkoutData.paymentDetailIdFromExternalAuth,
      totalAmount: this.basketData.totalAmountToPay,
      value: $checkoutData.paymentMethod
    };
  }

  public async validateBeforeSubmit() {
    this.$basketMethods.set("isProcessing", true);
    try {
      if (!(await this.validate())) throw this.$t("_.form_invalid");
      await this.$refs.checkoutProvider.doCheckout();
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.$basketMethods.set("isProcessing", false);
    }
  }

  // Life-cycle methods

  public async created() {
    try {
      if (!this.basket) {
        if (this.basketData.basketId !== BasketTypes.NEW) {
          this.$store.dispatch("toast/error", {
            message: this.$t("_sentence.basket_no_longer_exists")
          });
        }
        this.$shopMethods.goToShop();
      }

      // Get required basket fields
      await this.$basketMethods.getRequiredCustomFields();
      // Check whether checkout can be "entered"
      if (!this.basketData.canEnterCheckout)
        // Redirect back to basket
        return this.$shopMethods.goToBasket();
    } finally {
      this.isLoading = false;
    }
  }
}
